import type { PayloadAction } from '@reduxjs/toolkit'
// eslint-disable-next-line no-duplicate-imports
import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import {
  DefaultUserRoleFeature,
  IncomingUserEmployee,
  IncomingUserRole,
  MappedFeatureItem,
  OutgoingAddRoleDto,
  OutgoingAssignPermissionDto,
  OutgoingAssignPermissionEmployeeDto,
  UserRoleConfigurationModal,
  UserRolesConfigurationSliceState,
} from '../../../types/Settings/UserRolesConfiguration/UserRolesConfigurationTypes'
import userRolesConfigurationsApi from '../../../middleware/api/Settings/UserRolesConfiguration/UserRolesConfigurationApi'
import { ValidationError } from '../../../types/commonTypes'
import {
  initialUserEmployee,
  initialUserRole,
  initialUserRoleConfigurationModal,
} from '../../../pages/Settings/UserRolesConfiguration/UserRolesConfigurationsHelpers'
import { RootState } from '../../../stateStore'
import { emptyString } from '../../../constant/constantData'

let otherEmployeeId: string = emptyString

const initialUserRolesConfigurationsSliceState: UserRolesConfigurationSliceState =
  {
    isLoading: ApiLoadingState.idle,
    error: null,
    roles: [],
    featuresUnderRole: [], // this stores all the features
    featuresUnderEmployee: [],
    featuresUnderSelectedEmployee: [],
    subFeatures: [], // this stores the all the subfeatures of all features
    mappedFeatures: [],
    configurationModal: initialUserRoleConfigurationModal,
    selectedRole: initialUserRole,
    selectedEmployee: initialUserEmployee,
    hasLoadedPermissions: false,
    isUpdatingPermissions: false,
  }

const getUserRolesThunk = createAsyncThunk(
  'userRolesConfiguration/getUserRolesThunk',
  async (_, thunkApi) => {
    try {
      return await userRolesConfigurationsApi.getUserRoles()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const getFeaturesUnderRoleThunk = createAsyncThunk(
  'userRolesConfiguration/getFeaturesUnderRoleThunk',
  async (selectedRoleId: string, thunkApi) => {
    try {
      return await userRolesConfigurationsApi.getUserFeaturesUnderRole(
        selectedRoleId,
      )
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)
const getFeaturesUnderEmployeeThunk = createAsyncThunk(
  'userRolesConfiguration/getFeaturesUnderEmployeeThunk',
  async (selectedEmployeeId: string, thunkApi) => {
    try {
      otherEmployeeId = selectedEmployeeId
      return await userRolesConfigurationsApi.getUserFeaturesUnderEmployee(
        selectedEmployeeId,
      )
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const getFeaturesUnderVendorThunk = createAsyncThunk(
  'userRolesConfiguration/getUserFeaturesUnderVendor',
  async (selectedEmployeeId: string, thunkApi) => {
    try {
      otherEmployeeId = selectedEmployeeId
      return await userRolesConfigurationsApi.getUserFeaturesUnderVendor(
        selectedEmployeeId,
      )
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const getUserRoleSubFeatureThunk = createAsyncThunk(
  'userRolesConfiguration/getUserRoleSubFeaturesThunk',
  async (_, thunkApi) => {
    try {
      return await userRolesConfigurationsApi.getUserRoleSubFeatures()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const updateAssignPermissionsThunk = createAsyncThunk(
  'userRolesConfiguration/updateAssignPermissionsThunk',
  async (finalData: OutgoingAssignPermissionDto, thunkApi) => {
    try {
      return await userRolesConfigurationsApi.updateAssignPermissions(finalData)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const updateAssignEmployeePermissionsThunk = createAsyncThunk(
  'userRolesConfiguration/updateAssignEmployeePermissionsThunk',
  async (finalData: OutgoingAssignPermissionEmployeeDto, thunkApi) => {
    try {
      return await userRolesConfigurationsApi.updateAssignEmployeePermissions(
        finalData,
      )
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)
const deleteUserRoleThunk = createAsyncThunk(
  'userRolesConfiguration/deleteUserRoleThunk',
  async (roleId: number, thunkApi) => {
    try {
      return await userRolesConfigurationsApi.deleteUserRole(roleId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const isRoleExistsThunk = createAsyncThunk(
  'userRolesConfiguration/isRoleExistsThunk',
  async (roleName: string, thunkApi) => {
    try {
      return await userRolesConfigurationsApi.isRoleExists(roleName)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const addRoleThunk = createAsyncThunk(
  'userRolesConfiguration/addRoleThunk',
  async (finalParams: OutgoingAddRoleDto, thunkApi) => {
    try {
      return await userRolesConfigurationsApi.addRole(finalParams)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status)
    }
  },
)

const userRolesConfigurationsSlice = createSlice({
  name: 'userRolesConfiguration',
  initialState: initialUserRolesConfigurationsSliceState,
  reducers: {
    setMappedFeatures: (state, action: PayloadAction<MappedFeatureItem[]>) => {
      state.mappedFeatures = action.payload
    },
    setDisplayConfigurationModal: (state, action: PayloadAction<boolean>) => {
      state.configurationModal = {
        ...state.configurationModal,
        displayModal: action.payload,
      }
    },
    setConfigurationModal: (
      state,
      action: PayloadAction<UserRoleConfigurationModal>,
    ) => {
      state.configurationModal = action.payload
    },
    setSelectedRole: (state, action: PayloadAction<IncomingUserRole>) => {
      state.selectedRole = action.payload
    },
    setSelectedEmployee: (
      state,
      action: PayloadAction<IncomingUserEmployee>,
    ) => {
      state.selectedEmployee = action.payload
    },
    setIsUpdatingPermissions: (state, action) => {
      state.isUpdatingPermissions = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getUserRolesThunk.fulfilled, (state, action) => {
      state.roles = action.payload
    })
    builder.addCase(getFeaturesUnderRoleThunk.fulfilled, (state, action) => {
      state.featuresUnderRole = action.payload
    })

    builder.addCase(
      getFeaturesUnderEmployeeThunk.fulfilled,
      (state, action) => {
        const userId = localStorage.getItem('employeeId') as string

        if (String(userId) === String(state.selectedEmployee.EmployeeId)) {
          state.featuresUnderSelectedEmployee = action.payload
        } else if (
          userId === null ||
          String(otherEmployeeId) === String(userId)
        ) {
          state.featuresUnderEmployee = action.payload
        } else {
          state.featuresUnderSelectedEmployee = action.payload
        }
        state.hasLoadedPermissions = true
      },
    )
    builder.addCase(getFeaturesUnderVendorThunk.fulfilled, (state, action) => {
      const userId = localStorage.getItem('vendorEmployeeId') as string

      if (String(userId) === String(state.selectedEmployee.vendorEmployeeId)) {
        state.featuresUnderSelectedEmployee = action.payload
      } else if (
        userId === null ||
        String(otherEmployeeId) === String(userId)
      ) {
        state.featuresUnderEmployee = action.payload
      } else {
        state.featuresUnderSelectedEmployee = action.payload
      }
      state.hasLoadedPermissions = true
    })
    builder.addCase(getUserRoleSubFeatureThunk.fulfilled, (state, action) => {
      state.subFeatures = action.payload
    })
    builder.addMatcher(
      isAnyOf(
        getUserRolesThunk.pending,
        getFeaturesUnderRoleThunk.pending,
        getFeaturesUnderEmployeeThunk.pending,
        getFeaturesUnderVendorThunk.pending,
        getUserRoleSubFeatureThunk.pending,
        updateAssignPermissionsThunk.pending,
        updateAssignEmployeePermissionsThunk.pending,
        deleteUserRoleThunk.pending,
        isRoleExistsThunk.pending,
        addRoleThunk.pending,
      ),
      (state) => {
        state.isLoading = ApiLoadingState.loading
      },
    )
    builder.addMatcher(
      isAnyOf(
        getUserRolesThunk.fulfilled,
        getFeaturesUnderRoleThunk.fulfilled,
        getFeaturesUnderEmployeeThunk.fulfilled,
        getFeaturesUnderVendorThunk.fulfilled,
        getUserRoleSubFeatureThunk.fulfilled,
        updateAssignPermissionsThunk.fulfilled,
        updateAssignEmployeePermissionsThunk.fulfilled,
        deleteUserRoleThunk.fulfilled,
        isRoleExistsThunk.fulfilled,
        addRoleThunk.fulfilled,
      ),
      (state) => {
        state.isLoading = ApiLoadingState.succeeded
      },
    )
    builder.addMatcher(
      isAnyOf(
        getUserRolesThunk.rejected,
        getFeaturesUnderRoleThunk.rejected,
        getFeaturesUnderEmployeeThunk.rejected,
        getFeaturesUnderVendorThunk.rejected,
        getUserRoleSubFeatureThunk.rejected,
        updateAssignPermissionsThunk.rejected,
        updateAssignEmployeePermissionsThunk.rejected,
        deleteUserRoleThunk.rejected,
        isRoleExistsThunk.rejected,
        addRoleThunk.rejected,
      ),
      (state, action) => {
        state.isLoading = ApiLoadingState.failed
        state.error = action.payload as ValidationError
      },
    )
  },
})

const userPermissionsToFeatures = (
  state: RootState,
): DefaultUserRoleFeature[] =>
  state.userRolesConfiguration.featuresUnderEmployee

const selectedUserPermissionsToFeatures = (
  state: RootState,
): DefaultUserRoleFeature[] =>
  state.userRolesConfiguration.featuresUnderSelectedEmployee

const userHasLoadedPermissions = (state: RootState): boolean =>
  state.userRolesConfiguration.hasLoadedPermissions
const isUpdatingPermissions = (state: RootState): boolean =>
  state.userRolesConfiguration.isUpdatingPermissions

const userRolesConfigurationsSelectors = {
  userPermissionsToFeatures,
  selectedUserPermissionsToFeatures,
  userHasLoadedPermissions,
  isUpdatingPermissions,
}

const userRolesConfigurationsThunk = {
  getUserRolesThunk,
  getFeaturesUnderRoleThunk,
  getFeaturesUnderEmployeeThunk,
  getFeaturesUnderVendorThunk,
  getUserRoleSubFeatureThunk,
  updateAssignPermissionsThunk,
  deleteUserRoleThunk,
  isRoleExistsThunk,
  addRoleThunk,
  updateAssignEmployeePermissionsThunk,
}

export const userRolesConfigurationsServices = {
  ...userRolesConfigurationsThunk,
  actions: userRolesConfigurationsSlice.actions,
  selectors: userRolesConfigurationsSelectors,
}

const userRolesConfigurationReducer = userRolesConfigurationsSlice.reducer
export default userRolesConfigurationReducer
